// Company levels — the 4 customizable tiers below the company root. Read the
// current names/icons/SQL columns and bulk-save them. Saving invalidates
// `Levels` so the modal and the tree/chart (via useLevelConfig) refetch.

import { useMemo } from 'react';
import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import { getLevel } from '@/mocks/structure';

export interface Level {
  id: string;
  level: number;
  name: string;
  icon: string; // kebab-case lucide name
  sqlColumn: string;
  sqlColumnEnabled: boolean;
}

interface ApiLevel {
  _id: string;
  level: number;
  name: string;
  icon: string;
  sqlColumn: string;
  sqlColumnEnabled: boolean;
}

/** Picker option from GET /company-levels/selection (confirmed response:
 *  _id/icon/level/name only — no SQL-column config). */
export interface LevelOption {
  id: string;
  level: number;
  name: string;
  icon: string; // kebab-case lucide name
}

/** The bulk-update payload row (backend keys). */
export interface LevelUpdate {
  id: string;
  name: string;
  icon: string;
  sqlColumn: string;
  sqlColumnEnabled: boolean;
}

export const levelsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLevels: build.query<Level[], void>({
      query: () => ({ ...ROUTES.levels.list }),
      transformResponse: (res: ApiLevel[]) =>
        (res ?? [])
          .map((l) => ({
            id: l._id,
            level: l.level,
            name: l.name,
            icon: l.icon,
            sqlColumn: l.sqlColumn ?? '',
            sqlColumnEnabled: !!l.sqlColumnEnabled,
          }))
          .sort((a, b) => a.level - b.level),
      providesTags: ['Levels'],
    }),
    // Selection: only the levels the current user can reach through the
    // structure (name/level/icon only — no SQL-column config).
    getLevelsSelection: build.query<LevelOption[], void>({
      query: () => ({ ...ROUTES.levels.selection }),
      transformResponse: (res: Array<{ _id: string; icon: string; level: number; name: string }>) =>
        res
          .map((l) => ({ id: l._id, level: l.level, name: l.name, icon: l.icon }))
          .sort((a, b) => a.level - b.level),
      providesTags: [{ type: 'Levels', id: 'SELECTION' }],
    }),
    updateLevels: build.mutation<unknown, { levels: LevelUpdate[] }>({
      query: (body) => ({ ...ROUTES.levels.update, data: body }),
      invalidatesTags: ['Levels'],
    }),
  }),
});

export const { useGetLevelsQuery, useGetLevelsSelectionQuery, useUpdateLevelsMutation } = levelsApi;

/**
 * Resolve a numeric level → { name, icon, tone } for the tree/chart. Backed by
 * the cached getLevels query (RTK Query dedupes across callers). API name/icon
 * win when present; tone always comes from the frontend config (ORG_LEVELS).
 */
export const useLevelConfig = () => {
  const { data } = useGetLevelsQuery();
  const byLevel = useMemo(() => new Map((data ?? []).map((l) => [l.level, l])), [data]);
  return (level: number) => {
    const def = getLevel(level);
    const api = byLevel.get(level);
    return { name: api?.name ?? def.label, icon: api?.icon ?? def.icon, tone: def.tone };
  };
};
